import React, { useState } from 'react';
import type { DiscoveryProfileCard } from '../../services/api/discoveryService';
import { BlockReportMenu, type ReportReason } from '../common/BlockReportMenu';
import { ProfileCardTrigger } from '../profile/ProfileCardTrigger';
import { CompatibilityBadge } from './CompatibilityBadge';
import './SwipeCard.css';

interface SwipeCardProps {
  profile: DiscoveryProfileCard;
  onLike: () => void;
  onPass: () => void;
  onSuperLike: () => void;
  onWink?: () => void;
  onViewProfile?: () => void;
  onBlock?: (profileId: string) => Promise<void>;
  onReport?: (profileId: string, reason: ReportReason, details?: string) => Promise<void>;
}

export const SwipeCard: React.FC<SwipeCardProps> = ({
  profile,
  onLike,
  onPass,
  onSuperLike,
  onWink,
  onBlock,
  onReport,
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  // A sparse profile serializes its empty collections as null, not [] — never index
  // into them raw (profile.photos[0] crashed the whole Discovery page in prod).
  const photos = profile.photos ?? [];
  const prompts = profile.prompts ?? [];
  const interests = profile.interests ?? [];
  const currentPhoto = photos[currentPhotoIndex];

  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    }
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentPhotoIndex < photos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    }
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <div className="swipe-card">
      {/* Photo carousel */}
      <div className="swipe-card-photo" onClick={toggleDetails}>
        {currentPhoto ? (
          <img src={currentPhoto.url} alt={profile.displayName} />
        ) : (
          <div className="swipe-card-photo-placeholder" aria-hidden="true">
            {(profile.displayName || '?').charAt(0).toUpperCase()}
          </div>
        )}

        {/* Photo indicators */}
        {photos.length > 1 && (
          <div className="photo-indicators">
            {photos.map((_, index) => (
              <div
                key={index}
                className={`photo-indicator ${index === currentPhotoIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        )}

        {/* Photo navigation zones */}
        {currentPhotoIndex > 0 && (
          <div className="photo-nav photo-nav-prev" onClick={handlePrevPhoto} />
        )}
        {currentPhotoIndex < photos.length - 1 && (
          <div className="photo-nav photo-nav-next" onClick={handleNextPhoto} />
        )}

        {/* Prompt-answer overlay (V2 Amethyst WU-02, R4: "photo-first with
            prompt overlay") — the first answered prompt floats over the
            photo itself rather than being hidden behind the expand tap.
            Note: a "my character" avatar chip is not rendered here — the
            discovery feed's DiscoveryProfileCard carries no active-character
            data today (unlike ProfileCard's activeCharacter), and adding it
            means a new backend field; deliberately out of scope for this
            presentation-only pass, matching this WU's own "otherwise omit"
            precedent for data that doesn't exist yet. */}
        {prompts.length > 0 && (
          <div className="swipe-card-prompt-overlay">
            <p className="swipe-card-prompt-overlay-answer">"{prompts[0].answer}"</p>
          </div>
        )}

        {/* Verified badge */}
        {profile.isVerified && (
          <div className="verified-badge" title="Verified profile">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          </div>
        )}

        {/* Report/Block menu */}
        <div className="card-menu-container" onClick={(e) => e.stopPropagation()}>
          <BlockReportMenu
            profileId={profile.profileId}
            profileName={profile.displayName}
            onBlock={onBlock || (async () => { console.warn('Block handler not provided'); })}
            onReport={onReport || (async () => { console.warn('Report handler not provided'); })}
            className="swipe-card-menu"
          />
        </div>
      </div>

      {/* Profile info */}
      <div className={`swipe-card-info ${showDetails ? 'expanded' : ''}`}>
        <div className="info-header" onClick={toggleDetails}>
          {/* Tapping the name opens the unified ProfileCard (self-gates on
              social.profile_card; renders the name inertly when off). */}
          <ProfileCardTrigger profileId={profile.profileId}>
            <div className="name-age">
              <h2>{profile.displayName}</h2>
              <span className="age">{profile.age}</span>
            </div>
          </ProfileCardTrigger>
          {profile.compatibilityScore != null && (
            <CompatibilityBadge
              score={profile.compatibilityScore}
              sharedMemories={profile.sharedMemories}
            />
          )}
          {profile.locationCity && (
            <div className="location">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <span>
                {profile.locationCity}
                {profile.distanceKm !== null && profile.distanceKm !== undefined && (
                  <span className="distance"> - {Math.round(profile.distanceKm)} km away</span>
                )}
              </span>
            </div>
          )}
          <button className="expand-button" aria-label={showDetails ? 'Collapse' : 'Expand'}>
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className={showDetails ? 'rotated' : ''}
            >
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
            </svg>
          </button>
        </div>

        {/* Expanded details */}
        {showDetails && (
          <div className="info-details">
            {/* Bio */}
            {profile.bio && (
              <div className="bio-section">
                <p>{profile.bio}</p>
              </div>
            )}

            {/* Looking for */}
            {profile.relationshipType && (
              <div className="looking-for">
                <span className="label">Looking for:</span>
                <span className="value">{profile.relationshipType}</span>
              </div>
            )}

            {/* Shared memory overlaps (WU-B3 / §6). Renders only the backend's
                neutral canonical phrases — never raw memory text, never
                profile.interests. Visually distinct (accent/violet) from the
                rose Interests grid below. */}
            {profile.sharedMemories && profile.sharedMemories.length > 0 && (
              <div className="shared-interests-section">
                <h4>You both</h4>
                <div className="shared-interests-grid">
                  {profile.sharedMemories.map((sm) => (
                    <span key={sm.tag} className="shared-interest-tag" title={sm.npcName}>
                      {sm.phrase}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Interests */}
            {interests.length > 0 && (
              <div className="interests-section">
                <h4>Interests</h4>
                <div className="interests-grid">
                  {interests.map((interest, index) => (
                    <span key={index} className="interest-tag">
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Prompts */}
            {prompts.length > 0 && (
              <div className="prompts-section">
                {prompts.map((prompt, index) => (
                  <div key={index} className="prompt-card">
                    <p className="prompt-question">{prompt.question}</p>
                    <p className="prompt-answer">{prompt.answer}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Gaming info */}
            {profile.gamingFrequency && profile.gamingFrequency !== 'no' && (
              <div className="gaming-section">
                <h4>Gaming</h4>
                <div className="gaming-info">
                  <span className="gaming-frequency">
                    Plays {profile.gamingFrequency === 'yes' ? 'regularly' : profile.gamingFrequency}
                  </span>
                  {profile.gamingPlatforms.length > 0 && (
                    <div className="gaming-platforms">
                      {profile.gamingPlatforms.map((platform, index) => (
                        <span key={index} className="platform-tag">
                          {platform}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="swipe-actions">
        <button className="action-btn pass" onClick={onPass} aria-label="Pass">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>

        <button className="action-btn superlike" onClick={onSuperLike} aria-label="Super Like">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
        </button>

        <button className="action-btn like" onClick={onLike} aria-label="Like">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </button>

        {onWink && (
          <button className="action-btn wink" onClick={onWink} aria-label="Wink" title="Send a wink">
            <span aria-hidden="true">👋</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default SwipeCard;
